import React, { forwardRef, useState } from "react";
import styles from "./ExpertCards.module.css";
import star from "../../Assets/star.png";
import { Button, Tooltip, Chip, Avatar } from "@mui/material";
import ReadMore from "../ReadMore/ReadMore";
import { useNavigate } from "react-router-dom";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import VerifiedIcon from "@mui/icons-material/Verified";
import LanguageIcon from "@mui/icons-material/Language";

const ExpertCards = forwardRef(({ data }, ref) => {
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);

  const handleNavigateToProfile = (slug) => {
    navigate(`/experts/${slug}`);
  };

  const handleBookingNavigation = (slug) => {
    navigate(`/bookings/${slug}`);
  };

  const profileImage = data?.image || "default-profile-image-path.png";
  const fullName = `${data?.first_name || "Expert"} ${data?.last_name || "Name"}`;
  const hasSpecialties = data?.speciality_info?.length > 0;
  const hasLanguages = data?.language?.length > 0;
  const location = data?.location || "Remote Available";
  const isVerified = data?.is_verified || true;

  return (
    <div
      ref={ref}
      className={styles.cardContainer}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={() => setIsHovered(true)}
      onBlur={() => setIsHovered(false)}
    >
      <div className={styles.cardContent}>
        <div className={styles.left}>
          <div
            className={styles.imageContainer}
            role="img"
            aria-label={`Profile photo of ${fullName}`}
          >
            <Avatar
              src={profileImage}
              alt={fullName}
              className={styles.avatar}
            />
            {isVerified && (
              <div className={styles.verifiedBadge}>
                <VerifiedIcon fontSize="small" />
              </div>
            )}
          </div>
          <Tooltip title="See full profile details" arrow placement="top">
            <Button
              onClick={() => handleNavigateToProfile(data?.slug)}
              className={styles.viewProfileBtn}
              variant="outlined"
              size="small"
              aria-label={`View full profile for ${fullName}`}
            >
              View Profile
            </Button>
          </Tooltip>
        </div>

        <div className={styles.right}>
          <div className={styles.expertInfo}>
            <div className={styles.top}>
              <div className={styles.nameSection}>
                <h1>{fullName}</h1>
                <div className={styles.expertMeta}>
                  <p className={styles.experience}>
                    {data?.specialist_tagline
                      ? `${data.specialist_tagline}`
                      : ""}
                  </p>
                  {/* {location && (
                    <div className={styles.location}>
                      <LocationOnIcon fontSize="small" />
                      <span>{location}</span>
                    </div>
                  )} */}
                </div>
              </div>

              {data?.rating && (
                <Tooltip
                  title="Average rating from client reviews"
                  arrow
                  placement="top"
                >
                  <div className={styles.ratingContainer}>
                    <div className={styles.rating}>
                      <img src={star} alt="" aria-hidden="true" />
                      <span
                        aria-label={`Rated ${parseFloat(data.rating).toFixed(1)} out of 5`}
                      >
                        {parseFloat(data.rating).toFixed(1)}
                      </span>
                    </div>
                    {data?.reviews_count && (
                      <span className={styles.reviewCount}>
                        ({data.reviews_count} reviews)
                      </span>
                    )}
                  </div>
                </Tooltip>
              )}
            </div>

            <div className={styles.middle}>
              <ReadMore
                text={data?.about || "No description available."}
                maxLength={200}
              />

              {data?.availability && (
                <div className={styles.availability}>
                  <AccessTimeIcon fontSize="small" />
                  <span>Available: {data.availability}</span>
                </div>
              )}

              <div className={styles.specialtiesSection}>
                <div className={styles.sectionHeading}>
                  <strong>Specialties:</strong>
                </div>
                <div className={styles.labels} aria-label="Areas of expertise">
                  {hasSpecialties ? (
                    data.speciality_info.map((specialty, index) => (
                      <Chip
                        key={index}
                        label={specialty?.title}
                        size="small"
                        className={styles.specialtyChip}
                      />
                    ))
                  ) : (
                    <Chip
                      label="General Consultation"
                      size="small"
                      className={styles.specialtyChip}
                    />
                  )}
                </div>
              </div>

              {hasLanguages && (
                <div className={styles.specialtiesSection}>
                  <div className={styles.sectionHeading}>
                    <LanguageIcon
                      fontSize="small"
                      style={{ marginRight: "4px" }}
                    />
                    <strong>Languages:</strong>
                  </div>
                  <div className={styles.labels} aria-label="Languages spoken">
                    {data.language.map((lang, index) => (
                      <Chip
                        key={index}
                        label={lang?.name || lang}
                        size="small"
                        className={styles.specialtyChip}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className={styles.bottom}>
            <div className={styles.priceInfo}>
              {data?.hourly_rate && (
                <span className={styles.price}>${data.hourly_rate}/hr</span>
              )}
            </div>
            <Button
              variant="contained"
              className={styles.bookButton}
              onClick={() => handleBookingNavigation(data?.slug)}
              aria-label={`Book an appointment with ${fullName}`}
            >
              Book Appointment
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
});

export default ExpertCards;
